import { useCallback, useEffect, useRef } from 'react'
import { postIntegrityEvents, type IntegrityEvent, type IntegrityEventType } from '../api/client'

export interface UseProctoringResult {
  /** Best-effort flush of buffered events; called by the host on room disconnect. */
  flush: () => void
}

// Flush a pending batch once it reaches this many events, or when the timer
// fires — whichever comes first. Both bounds are well inside the 30/minute
// endpoint rate limit for a 10-minute interview.
const FLUSH_THRESHOLD = 10
const FLUSH_INTERVAL_MS = 15_000

/**
 * Collect integrity signals while a proctored interview is live: tab
 * switches (visibilitychange), window focus loss (blur/focus), and paste
 * attempts. Signals only — no camera, no screen capture, nothing blocking.
 *
 * Event timestamps are deltas from the moment this hook becomes enabled
 * (the candidate joined the room), measured with `performance.now()`
 * because it is monotonic — a mid-interview wall-clock correction can't
 * reorder the timeline the recruiter reads. A LiveKit reconnect restarts
 * the baseline; events across sessions of the same room stay internally
 * ordered, which is all the report needs.
 *
 * Events are buffered client-side and POSTed in batches so a chatty
 * visibility roller-coaster doesn't hammer the API; the effect cleanup
 * flushes the tail when the interview ends or the component unmounts,
 * mirroring the audio-upload's stop-on-disconnect ordering. A failed flush
 * re-queues its events (bounded, oldest dropped) and retries on the next
 * cycle — integrity data is advisory evidence, so losing a batch to a
 * network blip must not break the interview the way a failed audio upload
 * surfaces a destructive toast.
 */
export function useProctoring(
  enabled: boolean,
  sessionId: string | null,
  onEvent?: (event: IntegrityEvent) => void,
): UseProctoringResult {
  const bufferRef = useRef<IntegrityEvent[]>([])
  const dirtyRef = useRef(false)
  const flushingRef = useRef(false)
  const timerRef = useRef<number | null>(null)
  const onEventRef = useRef(onEvent)

  useEffect(() => {
    onEventRef.current = onEvent
  })

  const flush = useCallback(() => {
    if (!sessionId || flushingRef.current || bufferRef.current.length === 0) return
    flushingRef.current = true
    const batch = bufferRef.current
    bufferRef.current = []
    dirtyRef.current = false
    postIntegrityEvents(sessionId, batch).catch(() => {
      // Re-queue at the front so the timeline order survives; cap the
      // re-queue so repeated failures can't grow memory unbounded.
      const requeue = batch.slice(-FLUSH_THRESHOLD * 2)
      bufferRef.current = [...requeue, ...bufferRef.current].slice(-50)
      dirtyRef.current = true
    }).finally(() => {
      flushingRef.current = false
    })
  }, [sessionId])

  useEffect(() => {
    if (!enabled) return
    const startMs = performance.now()

    const record = (type: IntegrityEventType) => {
      const event = { type, at_ms: Math.max(0, Math.round(performance.now() - startMs)) }
      bufferRef.current.push(event)
      dirtyRef.current = true
      onEventRef.current?.(event)
      if (bufferRef.current.length >= FLUSH_THRESHOLD) flush()
    }

    // A tab switch fires both blur and visibilitychange; recording both is
    // intentional — blur alone also covers switching apps without leaving
    // the tab, and the report groups them rather than double-counting.
    const onVisibility = () => {
      if (document.hidden) record('tab_hidden')
      else record('tab_visible')
    }
    const onBlur = () => record('window_blur')
    const onFocus = () => record('window_focus')
    const onPaste = () => record('paste')

    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('blur', onBlur)
    window.addEventListener('focus', onFocus)
    document.addEventListener('paste', onPaste)

    timerRef.current = window.setInterval(() => {
      if (dirtyRef.current) flush()
    }, FLUSH_INTERVAL_MS)

    return () => {
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('blur', onBlur)
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('paste', onPaste)
      if (timerRef.current !== null) {
        window.clearInterval(timerRef.current)
        timerRef.current = null
      }
      // Live capture is over — deliver whatever is buffered so a short
      // interview that ends between timer ticks still reports fully.
      flush()
    }
  }, [enabled, flush])

  return { flush }
}
